import { useLocation, useNavigate } from 'react-router';
import { pushHistoryStateField } from './stateFields';

type LocationStateHook<TState> = [TState, (state: TState) => void];

type LocationState<TKey extends string, TState> = {
  [Key in TKey]: TState;
};

const undefinedUuid = 'ce2fb5e0-1f10-46c9-b9a2-70d9eb878f09';

/**
 * State field persisted in browser history
 */
export const useLocationState = <TState,>(
  id: string,
  initialState: TState,
): LocationStateHook<TState> => {
  const navigate = useNavigate();
  const location = useLocation();
  const stateKey = `useLocationState/${id}`;
  const locationState = location.state as
    | LocationState<typeof stateKey, TState>
    | undefined;

  const getState = () => {
    if (!locationState || locationState[stateKey] === undefined) {
      return initialState;
    }
    if (locationState[stateKey] === undefinedUuid) {
      // we know undefined is valid when undefined uuid has been saved
      return undefined as TState;
    }
    return locationState[stateKey];
  };

  const setState = (state: TState) => {
    pushHistoryStateField(navigate, location, {
      [stateKey]: state === undefined ? undefinedUuid : state,
    });
  };

  // Fixed noUncheckedIndexedAccess NIM-19408
  return [getState()!, setState];
};
